import { useState, useCallback, useEffect } from 'react';

const useQuestionNavigation = (totalQuestions) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

    // Navigate to next question
    const goToNext = useCallback(() => {
        setCurrentQuestionIndex(prev => {
            if (prev < totalQuestions - 1) {
                return prev + 1;
            }
            return prev;
        });
    }, [totalQuestions]);

    // Navigate to previous question
    const goToPrevious = useCallback(() => {
        setCurrentQuestionIndex(prev => {
            if (prev > 0) {
                return prev - 1;
            }
            return prev;
        });
    }, []);

    // Navigate to specific question
    const goToQuestion = useCallback((index) => {
        if (index >= 0 && index < totalQuestions) {
            setCurrentQuestionIndex(index);
        }
    }, [totalQuestions]);

    // Check if can navigate
    const canGoNext = currentQuestionIndex < totalQuestions - 1;
    const canGoPrevious = currentQuestionIndex > 0;

    // Reset index if total questions changes (e.g., when loading)
    useEffect(() => {
        if (currentQuestionIndex >= totalQuestions && totalQuestions > 0) {
            setCurrentQuestionIndex(0);
        }
    }, [totalQuestions, currentQuestionIndex]);

    // Navigate to first unreviewed question
    const goToFirstUnreviewed = useCallback((reviews, questions) => {
        if (!questions || questions.length === 0) return;
        
        const firstUnreviewedIndex = questions.findIndex(q => !reviews[q.item_id]);
        if (firstUnreviewedIndex !== -1) {
            setCurrentQuestionIndex(firstUnreviewedIndex);
        }
    }, []);

    // Navigate to next unreviewed question
    const goToNextUnreviewed = useCallback((reviews, questions) => {
        if (!questions || questions.length === 0) return;
        
        // Find next unreviewed after current index
        for (let i = currentQuestionIndex + 1; i < questions.length; i++) {
            if (!reviews[questions[i].item_id]) {
                setCurrentQuestionIndex(i);
                return;
            }
        }
        
        // If no unreviewed found after current, search from beginning
        for (let i = 0; i < currentQuestionIndex; i++) {
            if (!reviews[questions[i].item_id]) {
                setCurrentQuestionIndex(i);
                return;
            }
        }
    }, [currentQuestionIndex]);

    return {
        currentQuestionIndex,
        setCurrentQuestionIndex,
        goToNext,
        goToPrevious,
        goToQuestion,
        goToFirstUnreviewed,
        goToNextUnreviewed,
        canGoNext,
        canGoPrevious
    };
};

export default useQuestionNavigation;